import type { CompletionResult, StreamCallbacks, MessageContent } from '../types';

export const hasOpenAiApiKey = (): boolean => !!import.meta.env.VITE_OPENAI_API_KEY;

const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

/**
 * Get the OpenAI API key from environment
 */
const getApiKey = (): string => {
  const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
  if (!apiKey) {
    throw new Error('VITE_OPENAI_API_KEY is not configured');
  }
  return apiKey;
};

const buildHeaders = (apiKey: string): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${apiKey}`,
});

/**
 * Convert our MessageContent format to OpenAI's content format.
 * Anthropic uses {type: 'image', source: {type: 'base64', ...}},
 * OpenAI uses {type: 'image_url', image_url: {url: 'data:...'}}
 */
const convertUserContent = (content: MessageContent): unknown => {
  if (typeof content === 'string') {
    return content;
  }

  return content.map((block) => {
    if (block.type === 'text') {
      return { type: 'text' as const, text: block.text };
    }
    return {
      type: 'image_url' as const,
      image_url: {
        url: `data:${block.source.media_type};base64,${block.source.data}`,
      },
    };
  });
};

type OpenAIStreamEvent = {
  choices: Array<{
    delta: { content?: string; role?: string };
    finish_reason: string | null;
  }>;
};

/**
 * Make a non-streaming completion request to the OpenAI API
 */
export const openaiNonStreamingCompletion = async (params: {
  modelId: string;
  maxTokens: number;
  systemPrompt?: string;
  userPrompt: MessageContent;
  signal?: AbortSignal;
}): Promise<CompletionResult> => {
  const apiKey = getApiKey();

  const messages: Array<{ role: string; content: unknown }> = [];
  if (params.systemPrompt) {
    messages.push({ role: 'system', content: params.systemPrompt });
  }
  messages.push({ role: 'user', content: convertUserContent(params.userPrompt) });

  const response = await fetch(OPENAI_API_URL, {
    method: 'POST',
    headers: buildHeaders(apiKey),
    body: JSON.stringify({
      model: params.modelId,
      max_completion_tokens: params.maxTokens,
      messages,
    }),
    signal: params.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
  }

  const data = (await response.json()) as {
    choices: Array<{ message: { content: string }; finish_reason: string }>;
    usage: { prompt_tokens: number; completion_tokens: number };
  };

  const choice = data.choices[0];
  if (!choice) {
    throw new Error('No choices in OpenAI response');
  }

  return {
    text: choice.message.content,
    inputTokens: data.usage.prompt_tokens,
    outputTokens: data.usage.completion_tokens,
    stopReason: choice.finish_reason,
  };
};

/**
 * Make a streaming completion request to the OpenAI API
 */
export const openaiStreamCompletion = async (
  params: {
    modelId: string;
    maxTokens: number;
    systemPrompt?: string;
    userPrompt: MessageContent;
    signal?: AbortSignal;
  },
  callbacks: StreamCallbacks,
): Promise<void> => {
  const apiKey = getApiKey();

  const messages: Array<{ role: string; content: unknown }> = [];
  if (params.systemPrompt) {
    messages.push({ role: 'system', content: params.systemPrompt });
  }
  messages.push({ role: 'user', content: convertUserContent(params.userPrompt) });

  try {
    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: buildHeaders(apiKey),
      body: JSON.stringify({
        model: params.modelId,
        max_completion_tokens: params.maxTokens,
        stream: true,
        messages,
      }),
      signal: params.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      callbacks.onError(new Error(`OpenAI API error: ${response.status} - ${errorText}`));
      return;
    }

    if (!response.body) {
      callbacks.onError(new Error('Response body is null'));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      if (params.signal?.aborted) {
        reader.cancel();
        return;
      }

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) continue;

        const data = trimmed.slice(6);
        if (data === '[DONE]') continue;

        try {
          const event = JSON.parse(data) as OpenAIStreamEvent;
          const content = event.choices[0]?.delta?.content;
          if (content) {
            callbacks.onTextDelta(content);
          }
        } catch {
          // Skip unparseable SSE lines
        }
      }
    }

    // Process remaining buffer
    if (buffer.trim() && buffer.trim().startsWith('data: ')) {
      const data = buffer.trim().slice(6);
      if (data !== '[DONE]') {
        try {
          const event = JSON.parse(data) as OpenAIStreamEvent;
          const content = event.choices[0]?.delta?.content;
          if (content) {
            callbacks.onTextDelta(content);
          }
        } catch {
          // Skip
        }
      }
    }

    callbacks.onComplete();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return;
    }
    callbacks.onError(error instanceof Error ? error : new Error(String(error)));
  }
};
